import { LitElement, html, css } from '@hh/global.js'

class HHCheckboxWrapper extends LitElement {
  static properties = {
    checked: {
      type: Boolean,
      reflect: true,
    },
    disabled: {
      type: Boolean,
      reflect: true,
    },
    useIcon: {
      type: Boolean,
      reflect: true,
      attribute: 'use-icon',
    },
  }

  constructor() {
    super()

    if (this.useIcon === undefined) {
      this.useIcon = true
    }
  }

  connectedCallback() {
    super.connectedCallback()

    this.checked = this.querySelector('input')?.checked
  }

  firstUpdated() {
    this.input.checked = this.checked
    this.input.disabled = this.disabled
  }

  updated(changedProperties) {
    if (changedProperties.has('disabled')) {
      this.input.disabled = this.disabled
    }
    if (changedProperties.has('checked')) {
      this.input.checked = this.checked
    }
  }

  onChange(e) {
    this.checked = e?.target?.checked
  }

  get input() {
    const slot = this.shadowRoot?.querySelector(`slot[name='input']`)
    return slot?.assignedNodes()[0] ?? null
  }

  render() {
    return html`
      <slot @change="${this.onChange}" name="input"></slot>
      <slot name="label"></slot>
      <slot name="checkmark"></slot>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    :host {
      position: relative;
      display: block;
    }

    ::slotted(input) {
      width: var(--hh-width, 16px);
      height: var(--hh-height, 16px);
      position: absolute;
      top: 2px;
      left: 0;
      border: none;
      appearance: none;
      padding: 0;
      margin: 0;
      border-radius: var(--hh-border-radius, 1px);
    }

    ::slotted(input:focus-visible) {
      outline-offset: 0;
    }

    ::slotted(label) {
      position: relative;
      cursor: pointer;
      color: var(--hh-label-color, #000);
    }

    :host([disabled]) ::slotted(label) {
      color: var(--hh-color-disabled, #000);
      cursor: default;
    }

    :host(:not([use-icon])) ::slotted([slot='checkmark']) {
      display: none;
    }

    :host([checked]) ::slotted(label) {
      color: var(--hh-label-color-active, #000);
    }

    @media (hover: hover) {
      :host(:hover:not([disabled])) ::slotted(label) {
        color: var(--hh-label-color-hover, #000);
      }
    }

    ::slotted([slot='checkmark']) {
      scale: 0;
      display: flex;
      pointer-events: none;
      position: absolute;
      top: var(--hh-icon-checkmark-top, 0.4rem);
      left: var(--hh-icon-checkmark-left, 0.4rem);
      width: var(--hh-width, 1.6rem);
      height: var(--hh-height, 1.6rem);
      max-width: var(--hh-icon-checkmark-max-width, 1.6rem);
      align-items: center;
      justify-content: center;
    }

    :host([checked]) ::slotted([slot='checkmark']) {
      scale: 1;
    }

    ::slotted(label):before {
      content: '';
      -webkit-appearance: none;
      background-color: transparent;
      border: 1px solid var(--hh-border-color, #000);
      width: var(--hh-width, 16px);
      height: var(--hh-height, 16px);
      display: inline-block;
      position: relative;
      vertical-align: middle;
      cursor: pointer;
      margin-right: var(--hh-gap, 5px);
      border-radius: var(--hh-border-radius, 1px);
    }

    :host([disabled]) ::slotted(label):before {
      border-color: var(--hh-color-disabled, #000);
      cursor: default;
    }

    :host([checked]) ::slotted(label):before {
      border-color: var(--hh-border-color-active, #000);
    }

    @media (hover: hover) {
      :host(:hover:not([disabled])) ::slotted(label):before {
        border-color: var(--hh-border-color-active, #000);
      }
    }

    :host([checked][use-icon]) ::slotted(label)::after {
      display: none;
    }

    :host([checked]) ::slotted(label):after {
      content: '';
      display: block;
      position: absolute;
      top: var(--hh-check-top, 2px);
      left: var(--hh-check-left, 6px);
      width: var(--hh-check-width, 3px);
      height: var(--hh-check-height, 8px);
      border: solid var(--hh-color, #000);
      border-width: 0 var(--hh-check-border-width, 1px)
        var(--hh-check-border-width, 1px) 0;
      transform: rotate(45deg);
    }
  `
}

customElements.define('hh-checkbox-wrapper', HHCheckboxWrapper)
